import * as L from 'leaflet';
import { inject, Injectable } from '@angular/core';
import { MapLayer } from '../interfaces/map';
import {
  RouteNetwork,
  RouteNetworkMaintainer,
  RouteNetworkSegment,
  RouteNetworkSegmentLine,
} from '../interfaces/map-data';
import { LayerBuilderService } from './layer-builder.service';
import LayerColorUtils from '../utils/layer-color.utils';
import { RouteSegmentType } from '../constants/constants';
import MapZoomRulesUtils from '../utils/map-zoom-rules.utils';

export interface RouteSegmentRedraw {
  network: RouteNetwork;
  segment: RouteNetworkSegment;
  layersToRemove: MapLayer[];
}

@Injectable({ providedIn: 'root' })
export class MapLayerProcessingService {
  private layerBuilderService = inject(LayerBuilderService);

  processUpdatedMaintainerLayers(
    cachedMaintainers: MapLayer[],
    maintainersUpdate: RouteNetworkMaintainer[],
    map: L.Map,
    areMaintainersHidden: boolean,
  ): MapLayer[] {
    maintainersUpdate.forEach((m) => {
      const id = String(m.TrackerObjectID);

      // Find all cached markers with matching trackerObjectID
      const matches = cachedMaintainers.filter(
        (marker) => String(marker.extraData?.trackerObjectID) === id,
      );

      if (matches.length > 1) {
        // Remove all duplicates but keep the last one
        const toRemove = matches.slice(0, -1);
        toRemove.forEach((mark) => {
          map.removeLayer(mark);
          cachedMaintainers.splice(cachedMaintainers.indexOf(mark), 1);
        });
      }

      // After dedupe we might have 0 or 1 remaining marker
      const remaining = cachedMaintainers.find(
        (marker) => String(marker.extraData?.trackerObjectID) === id,
      );

      const hasCoords = m.LastGPSLat && m.LastGPSLon;
      if (hasCoords) {
        // If the updated marker has coordinates, remove the last remaining one too
        if (remaining) {
          map.removeLayer(remaining);
          cachedMaintainers.splice(cachedMaintainers.indexOf(remaining), 1);
        }

        // Finally add the new updated marker
        const newLayer = this.layerBuilderService.buildRouteMaintainerLayers([m])[0];
        if (!areMaintainersHidden) {
          map.addLayer(newLayer);
        }
        cachedMaintainers.push(newLayer);
      }

      // If no coordinates → keep the one remaining marker as-is
    });

    return cachedMaintainers;
  }

  addOrRemoveNetworkTooltips(map: L.Map, clusterGroup: L.MarkerClusterGroup): void {
    const zoom = map.getZoom();
    const bounds = map.getBounds();

    if (!clusterGroup) return;

    if (zoom < 12) {
      clusterGroup.eachLayer((layer: MapLayer) => {
        if (
          layer.extraData?.isRouteNetworkMarker &&
          layer instanceof L.Marker &&
          layer.getTooltip()
        ) {
          layer.unbindTooltip();
        }
      });
      return;
    }

    clusterGroup.eachLayer((layer: MapLayer) => {
      const data = layer.extraData;
      if (!data?.isRouteNetworkMarker) return;
      if (!(layer instanceof L.Marker)) return;

      const latLng = layer.getLatLng();
      if (!bounds.contains(latLng)) {
        if (layer.getTooltip()) layer.unbindTooltip();
        return;
      }

      if (!layer.getTooltip()) {
        const content = data.name || '';
        layer.bindTooltip(content, {
          direction: 'left',
          permanent: true,
          className: 'route-network-marker-tooltip',
          offset: [-15, 0],
          opacity: 0.8,
        });
        layer.openTooltip();
      }
    });
  }

  addOrRemoveSegmentLineTooltips(map: L.Map): void {
    const zoom = map.getZoom();
    const bounds = map.getBounds();
    const layers: MapLayer[] = (map as any)._layers;
    const segmentLineMarkers = [];

    for (const id in layers) {
      const layer = layers[id];
      map;
      if (
        !layer.extraData?.isRouteEdge &&
        !layer.extraData?.isRouteHighlight &&
        layer instanceof L.Polyline
      ) {
        segmentLineMarkers.push(layer);
      }
    }

    if (zoom < 15) {
      segmentLineMarkers.forEach((layer: MapLayer) => {
        if (layer instanceof L.Polyline && layer.getTooltip()) {
          layer.unbindTooltip();
        }
      });
      return;
    }

    segmentLineMarkers.forEach((layer: MapLayer) => {
      const data = layer.extraData;
      if (!(layer instanceof L.Polyline)) return;

      const latLngs = layer.getLatLngs() as L.LatLng[];
      const isVisible = latLngs.some((p) => bounds.contains(p));

      if (!isVisible) {
        if (layer.getTooltip()) layer.unbindTooltip();
        return;
      }

      if (!layer.getTooltip()) {
        layer.bindTooltip(
          `SID: <b>${data?.routeSegmentId}</b>, SLID: <b>${data?.routeSegmentLineId}</b>`,
          {
            direction: 'left',
            permanent: true,
            className: 'basic-tooltip',
          },
        );
        layer.openTooltip();
      }
    });
  }

  /** method consumes response from routeNetwork websocket stream and updates
   * polyline data if changed. Walking trails are drawn as colour runs, so a colour change
   * can split or join their polylines - those segments are returned to be redrawn instead.
   */
  findAndUpdateChangedPolylineLayerColors(
    routeNetworksUpdate: RouteNetwork[],
    allMapPolylines: MapLayer[],
  ): RouteSegmentRedraw[] {
    const segmentsToRedraw: RouteSegmentRedraw[] = [];

    // update segments (polylines)
    routeNetworksUpdate.forEach((routeNetworkToUpdate) => {
      routeNetworkToUpdate.RouteSegments?.forEach((newSegmentData) => {
        const allSegmentLayers = allMapPolylines.filter(
          (polyline) => polyline.extraData?.routeSegmentId === newSegmentData.RouteSegmentID,
        );
        const allSegmentPolylines = allSegmentLayers.filter(
          (polyline) =>
            !polyline.extraData?.isRouteEdge && !polyline.extraData?.isWalkingSegmentMarker,
        );
        if (!allSegmentPolylines.length) return;

        const drawnTypeId = allSegmentPolylines[0].extraData?.routeSegmentTypeId;
        const newTypeId = newSegmentData.RouteSegmentTypeID ?? drawnTypeId;
        const features =
          newSegmentData.RouteSegmentFeatures ??
          allSegmentPolylines[0].extraData?.routeSegmentFeatures;
        const segmentLines = this.mergeSegmentLines(
          allSegmentPolylines[0].extraData?.segmentLines ?? [],
          newSegmentData.Lines ?? [],
        );

        if (drawnTypeId === RouteSegmentType.Walking || newTypeId === RouteSegmentType.Walking) {
          segmentsToRedraw.push({
            network: routeNetworkToUpdate,
            segment: {
              ...newSegmentData,
              RouteSegmentTypeID: newTypeId,
              RouteSegmentFeatures: features,
              Lines: segmentLines,
            },
            layersToRemove: allSegmentLayers,
          });
          return;
        }

        allSegmentPolylines.forEach((polyline) => {
          polyline.extraData = {
            ...polyline.extraData,
            routeSegmentTypeId: newTypeId,
            routeSegmentFeatures: features,
            segmentLines,
          };
        });
        // update only those polylines colors, that currently exist on the map
        const segmentLinesToUpdate = allSegmentPolylines.filter((polyline) =>
          newSegmentData.Lines?.find(
            (line) => line.RouteSegmentLineID === polyline.extraData?.routeSegmentLineId,
          ),
        );

        segmentLinesToUpdate.forEach((polyline) => {
          const newLineData = newSegmentData.Lines?.find(
            (line) => line.RouteSegmentLineID === polyline.extraData?.routeSegmentLineId,
          );

          if (newLineData) {
            (polyline as L.Polyline).setStyle({
              color: LayerColorUtils.getRouteColorOrIcon(newLineData.LastMaintainedTimeUTC),
            });
            (polyline as L.Polyline).bindPopup(
              this.layerBuilderService.createSegmentPopupHtml({
                ...routeNetworkToUpdate,
                /** override network last maintained time to show line last maintained time */
                LastMaintainedTimeUTC: newLineData.LastMaintainedTimeUTC,
              }),
            );
          }
        });
      });
    });

    return segmentsToRedraw;
  }

  /** walking segment markers are shown from POI zoom, unless the segment is shorter than 64 px on screen
   * or the marker plus 4 px overlaps another marker. When walking segment markers overlap each other,
   * the one on the longer segment is shown.
   */
  updateWalkingSegmentMarkers(map: L.Map): void {
    const walkingMarkers: L.Marker[] = [];
    const occupiedRects: DOMRect[] = [];

    map.eachLayer((layer) => {
      if (!(layer instanceof L.Marker)) return;

      const extraData = (layer as MapLayer).extraData;
      if (extraData?.isWalkingSegmentMarker) {
        walkingMarkers.push(layer);
      } else if (this.isWalkingSegmentMarkerObstacle(layer)) {
        const rect = layer.getElement()?.getBoundingClientRect();
        if (rect) occupiedRects.push(rect);
      }
    });

    const zoom = map.getZoom();
    const markersVisible = !MapZoomRulesUtils.hidePOI(zoom);
    const mapRect = map.getContainer().getBoundingClientRect();
    const shownCenters: L.Point[] = [];

    walkingMarkers
      .sort(
        (a, b) =>
          ((b as MapLayer).extraData?.segmentLengthMeters ?? 0) -
          ((a as MapLayer).extraData?.segmentLengthMeters ?? 0),
      )
      .forEach((marker) => {
        const latLng = marker.getLatLng();
        // web mercator: meters per screen pixel at this latitude and zoom
        const metersPerPixel =
          (40075016.686 * Math.cos((latLng.lat * Math.PI) / 180)) / Math.pow(2, zoom + 8);
        const segmentLengthPx =
          ((marker as MapLayer).extraData?.segmentLengthMeters ?? 0) / metersPerPixel;
        const center = map.latLngToContainerPoint(latLng).add(L.point(mapRect.left, mapRect.top));
        // 20 px marker plus 4 px on every side
        const halfSize = 14;

        const isVisible =
          markersVisible &&
          segmentLengthPx >= 64 &&
          !occupiedRects.some(
            (rect) =>
              center.x - halfSize < rect.right &&
              center.x + halfSize > rect.left &&
              center.y - halfSize < rect.bottom &&
              center.y + halfSize > rect.top,
          ) &&
          !shownCenters.some((shown) => shown.distanceTo(center) < 20);

        if (isVisible) shownCenters.push(center);
        marker.setOpacity(isVisible ? 1 : 0);
        // hidden markers must not catch clicks meant for the trail under them
        marker.getElement()?.style.setProperty('pointer-events', isVisible ? '' : 'none');
      });
  }

  /** POIs, network markers and clusters, snow groomers and the shared-link pin */
  private isWalkingSegmentMarkerObstacle(marker: L.Marker): boolean {
    const extraData = (marker as MapLayer).extraData;
    return (
      'getChildCount' in marker ||
      !!extraData?.isPoiMarker ||
      !!extraData?.isRouteNetworkMarker ||
      extraData?.trackerObjectID !== undefined ||
      !!extraData?.isSharedLinkPin
    );
  }

  /** updates may only contain the changed lines of a segment */
  private mergeSegmentLines(
    currentLines: RouteNetworkSegmentLine[],
    updatedLines: RouteNetworkSegmentLine[],
  ): RouteNetworkSegmentLine[] {
    const linesById = new Map(currentLines.map((line) => [line.RouteSegmentLineID, line]));
    updatedLines.forEach((line) => linesById.set(line.RouteSegmentLineID, line));

    return [...linesById.values()].sort((a, b) => (a.SequenceNo ?? 0) - (b.SequenceNo ?? 0));
  }

  removeApiOriginLayersFromMap(
    layers: MapLayer[],
    mapComponent: L.Map,
    canRemove?: (layer: MapLayer) => boolean,
  ): void {
    if (!layers.length) return;

    layers.forEach((layer) => {
      if (layer.extraData?.origin !== 'user' && (!canRemove || canRemove(layer))) {
        mapComponent.removeLayer(layer);
      }
    });
  }

  removeRouteNetworkLayersFromMap(mapComponent: L.Map) {
    mapComponent.eachLayer((layer) => {
      const extraData = (layer as MapLayer).extraData;
      // remove all existing polylines and route network markers from the map, before loading new part of the map
      // ensure user created layers are not removed
      if (
        (layer instanceof L.Marker &&
          (extraData?.isRouteNetworkMarker || extraData?.isWalkingSegmentMarker)) ||
        (layer instanceof L.Polyline && extraData?.origin !== 'user')
      ) {
        mapComponent.removeLayer(layer);
      }
    });
  }
}
